import { useEffect, useCallback } from 'react';
import { useApiGet } from '../../../hooks/useApiQuery';
import { useAuthReady } from '../../../hooks/useAuthReady';
import { useQueryClient } from '@tanstack/react-query';

export type {
  SessionContext,
  TimelineItem,
  AlertsSummary,
  SignOff,
  EnrichedBriefingData,
  BriefingSession,
} from './useDashboardBriefing.types';

import type { EnrichedBriefingData } from './useDashboardBriefing.types';

/** Practitioner/clinician character types that have a practitioner profile. */
const THERAPIST_CHARACTER_TYPES = [
  'therapist', 'student_therapist', 'psychiatric_np',
  'supervisor', 'clinical_director',
];

/**
 * Check if user is a client (briefing should NOT show for clients).
 */
function isClientUser(user: Record<string, unknown> | null): boolean {
  if (!user) return false;
  if (user.demo_mode) {
    const clientTypes = ['client', 'parent_of_client', 'part_of_couple'];
    return clientTypes.includes(user.character_type as string);
  }
  const roles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
  return roles.includes('client');
}

function isMedicalAssistant(user: Record<string, unknown> | null): boolean {
  if (!user) return false;
  if (user.demo_mode) {
    return user.character_type === 'medical_assistant';
  }
  const roles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
  return roles.includes('medical_assistant');
}

/** Check if user is a therapist/clinician with a practitioner profile. */
function isTherapistUser(user: Record<string, unknown> | null): boolean {
  if (!user) return false;
  if (user.demo_mode) {
    return THERAPIST_CHARACTER_TYPES.includes(user.character_type as string);
  }
  const roles = Array.isArray(user.roles) ? user.roles : (user.role ? [user.role] : []);
  return roles.some(r => THERAPIST_CHARACTER_TYPES.includes(r as string));
}

/**
 * Hook that fetches the enriched therapist briefing data for the dashboard.
 * Uses the enriched endpoint which includes per-session clinical context.
 */
export function useDashboardBriefing(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const { ready, user } = useAuthReady();
  const isClient = isClientUser(user as Record<string, unknown> | null);
  const isMA = isMedicalAssistant(user as Record<string, unknown> | null);
  const isTherapist = isTherapistUser(user as Record<string, unknown> | null);
  const shouldFetch = enabled && ready && !!user && !isClient;

  const queryClient = useQueryClient();

  const { data: briefingData = null, isLoading, error: fetchError } = useApiGet<EnrichedBriefingData>(
    shouldFetch ? ['dashboard', 'briefing', 'enriched'] : null,
    '/api/therapist_dashboard/briefing/enriched',
    { refetchInterval: 5 * 60 * 1000 },
  );

  const loading = shouldFetch ? isLoading : false;
  const error = fetchError ? 'Failed to load briefing' : null;

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'briefing', 'enriched'] });
  }, [queryClient]);

  // Re-fetch when calendar mutations signal staleness
  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener('briefing-stale', handler);
    return () => window.removeEventListener('briefing-stale', handler);
  }, [refetch]);

  return {
    briefingData,
    loading,
    error,
    isClient,
    isMA,
    isTherapist,
    shouldFetch,
    refetch,
  };
}
